import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../../services/api';
import { useToast } from '../../contexts/ToastContext';

const AdminSettings = () => {
  const [brands,      setBrands]     = useState([]);
  const [loadingSort, setLoadingSort]= useState(true);
  const [direction,   setDirection]  = useState(() => localStorage.getItem('brand_sort_dir') || '');
  const [applying,    setApplying]   = useState(false);

  const [pwForm,      setPwForm]     = useState({ current_password: '', new_password: '', confirm_password: '' });
  const [savingPw,    setSavingPw]   = useState(false);

  const toast = useToast();

  const fetchBrands = useCallback(async () => {
    setLoadingSort(true);
    try {
      const res = await api.getBrands();
      setBrands(res.data);
    } catch {
      toast.error('Errore nel caricamento dei brand.');
    } finally {
      setLoadingSort(false);
    }
  }, [toast]);

  useEffect(() => { fetchBrands(); }, [fetchBrands]);

  const handleDirectionChange = async (dir) => {
    if (!dir || dir === direction) return;

    setApplying(true);
    try {
      const sorted = [...brands].sort((a, b) =>
        dir === 'asc'
          ? a.name.localeCompare(b.name, 'it', { sensitivity: 'base' })
          : b.name.localeCompare(a.name, 'it', { sensitivity: 'base' })
      );
      const items = sorted.map((b, i) => ({ id: b.id, sort_order: i + 1 }));
      await api.sortBrands(items);

      setBrands(sorted);
      setDirection(dir);
      localStorage.setItem('brand_sort_dir', dir);
      toast.success(`Brand ordinati in ordine ${dir === 'asc' ? 'crescente' : 'decrescente'}.`);
    } catch (err) {
      toast.error(err.message || 'Errore durante l\'ordinamento.');
    } finally {
      setApplying(false);
    }
  };

  const handlePwSubmit = async (e) => {
    e.preventDefault();
    if (pwForm.new_password !== pwForm.confirm_password) {
      toast.error('Le nuove password non coincidono.');
      return;
    }
    if (pwForm.new_password.length < 8) {
      toast.error('La password deve avere almeno 8 caratteri.');
      return;
    }
    setSavingPw(true);
    try {
      await api.changePassword(pwForm);
      toast.success('Password aggiornata con successo.');
      setPwForm({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      toast.error(err.message || 'Errore durante il cambio password.');
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-2xl space-y-10">
      <h1 className="text-2xl text-white font-semibold">Impostazioni</h1>

      {/* ── Ordinamento brand ─────────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg text-white font-medium mb-1">Ordinamento brand</h2>
        <p className="text-gray-500 text-xs mb-4">
          Ordina automaticamente i brand per nome. La modifica è applicata subito sul sito.
        </p>

        <div className="bg-gray-900 border border-gray-700 rounded-lg p-4 flex items-center gap-4">
          <label className="text-sm text-gray-300 shrink-0">Ordine alfabetico</label>
          <select
            value={direction}
            onChange={e => handleDirectionChange(e.target.value)}
            disabled={applying || loadingSort}
            className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white transition disabled:opacity-50"
          >
            <option value="" disabled>Seleziona…</option>
            <option value="asc">Crescente (A → Z)</option>
            <option value="desc">Decrescente (Z → A)</option>
          </select>
          {applying && <span className="text-xs text-gray-500 shrink-0">Salvataggio…</span>}
        </div>

        {/* Anteprima ordine corrente */}
        {!loadingSort && brands.length > 0 && (
          <div className="mt-4">
            <p className="text-xs text-gray-500 mb-2">Ordine attuale:</p>
            <div className="flex flex-wrap gap-2">
              {brands.map((b, i) => (
                <span
                  key={b.id}
                  className={`text-xs px-2 py-1 rounded border ${
                    b.is_active ? 'border-gray-700 text-gray-300' : 'border-gray-800 text-gray-600'
                  }`}
                >
                  {i + 1}. {b.name}
                </span>
              ))}
            </div>
          </div>
        )}
      </section>

      <hr className="border-gray-800" />

      {/* ── Cambio password ──────────────────────────────────────────────── */}
      <section>
        <h2 className="text-lg text-white font-medium mb-4">Cambio password</h2>
        <form onSubmit={handlePwSubmit} className="space-y-4">
          {[
            { key: 'current_password', label: 'Password attuale' },
            { key: 'new_password',     label: 'Nuova password' },
            { key: 'confirm_password', label: 'Conferma nuova password' },
          ].map(({ key, label }) => (
            <div key={key}>
              <label className="block text-xs text-gray-400 mb-1">{label}</label>
              <input
                type="password"
                value={pwForm[key]}
                onChange={e => setPwForm(f => ({ ...f, [key]: e.target.value }))}
                required
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm focus:outline-none focus:border-white transition"
              />
            </div>
          ))}

          <button
            type="submit"
            disabled={savingPw}
            className="px-5 py-2 bg-white text-black rounded-lg text-sm font-medium hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {savingPw ? 'Salvataggio...' : 'Aggiorna password'}
          </button>
        </form>
      </section>
    </div>
  );
};

export default AdminSettings;
